import { useState, useEffect, useRef } from 'react'
import { listFiles, uploadFile, deleteFile, deleteFolder } from '../firebase/files'
import './Files.css'

const Files = ({ user, filesReadOnly = false }) => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, fileName: '', filePath: '', isFolder: false })
  const [dragActive, setDragActive] = useState(false)
  const [currentPath, setCurrentPath] = useState('CRM Shared Documents')
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!user) return
    loadFiles()
  }, [user, currentPath])

  const loadFiles = async () => {
    try {
      setLoading(true)
      const filesList = await listFiles(currentPath)
      setFiles(filesList)
    } catch (error) {
      console.error('Error loading files:', error)
      alert(`Failed to load files: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = async (selectedFiles) => {
    if (filesReadOnly || !selectedFiles || selectedFiles.length === 0) return

    const filesArray = Array.from(selectedFiles)
    
    try {
      setUploading(true)
      setUploadProgress(0)

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i]
        try {
          await uploadFile(file, currentPath)
          setUploadProgress(((i + 1) / filesArray.length) * 100)
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error)
          alert(`Failed to upload ${file.name}: ${error.message}`)
        }
      }

      // Reload files list after upload
      await loadFiles()
      alert(`Successfully uploaded ${filesArray.length} file(s)`)
    } catch (error) {
      console.error('Error uploading files:', error)
      alert(`Failed to upload files: ${error.message}`)
    } finally {
      setUploading(false)
      setUploadProgress(0)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileInputChange = (e) => {
    const selectedFiles = e.target.files
    if (selectedFiles) {
      handleFileSelect(selectedFiles)
    }
  }

  const handleDrag = (e) => {
    if (filesReadOnly) return
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    if (filesReadOnly) return
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  const handleDeleteClick = (fileName, filePath, isFolder) => {
    if (filesReadOnly) return
    setDeleteConfirm({ show: true, fileName, filePath, isFolder })
  }

  const confirmDelete = async () => {
    const { filePath, isFolder } = deleteConfirm
    
    try {
      if (isFolder) {
        await deleteFolder(filePath)
        alert('Folder deleted successfully')
      } else {
        await deleteFile(filePath)
        alert('File deleted successfully')
      }
      await loadFiles()
    } catch (error) {
      console.error('Error deleting:', error)
      alert(`Failed to delete ${isFolder ? 'folder' : 'file'}: ${error.message}`)
    } finally {
      setDeleteConfirm({ show: false, fileName: '', filePath: '', isFolder: false })
    }
  }

  const handleFolderClick = (folderPath) => {
    setCurrentPath(folderPath)
  }

  const handleNavigateUp = () => {
    if (currentPath === 'CRM Shared Documents') return
    
    const pathParts = currentPath.split('/')
    pathParts.pop()
    setCurrentPath(pathParts.join('/') || 'CRM Shared Documents')
  }

  const getBreadcrumbs = () => {
    const parts = currentPath.split('/')
    return parts.map((part, index) => ({
      name: part,
      path: parts.slice(0, index + 1).join('/')
    }))
  }

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, fileName: '', filePath: '', isFolder: false })
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDownload = (file) => {
    if (file.downloadURL && !file.isFolder) {
      window.open(file.downloadURL, '_blank')
    }
  }

  if (loading) {
    return <div className="loading">Loading files...</div>
  }

  return (
    <div className="files-page">
      <div className="page-header">
        <div>
          <h2>Files</h2>
          <p className="subtitle">
            {files.filter(f => f.isFolder).length} folder{files.filter(f => f.isFolder).length !== 1 ? 's' : ''}, {' '}
            {files.filter(f => !f.isFolder).length} file{files.filter(f => !f.isFolder).length !== 1 ? 's' : ''}
          </p>
        </div>
        {!filesReadOnly && (
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : '+ Upload Files'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>
        )}
      </div>

      {currentPath !== 'CRM Shared Documents' && (
        <div className="breadcrumbs">
          <button className="breadcrumb-btn" onClick={handleNavigateUp}>
            ← Back
          </button>
          <div className="breadcrumb-path">
            {getBreadcrumbs().map((crumb, index) => (
              <span key={index}>
                {index > 0 && <span className="breadcrumb-separator"> / </span>}
                {index === getBreadcrumbs().length - 1 ? (
                  <span className="breadcrumb-current">{crumb.name}</span>
                ) : (
                  <button
                    className="breadcrumb-link"
                    onClick={() => setCurrentPath(crumb.path)}
                  >
                    {crumb.name}
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {!filesReadOnly && uploading && (
        <div className="upload-progress">
          <div className="upload-progress-bar">
            <div 
              className="upload-progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p>Uploading... {Math.round(uploadProgress)}%</p>
        </div>
      )}

      <div
        className={`upload-area ${filesReadOnly ? 'upload-area-readonly' : ''} ${!filesReadOnly && dragActive ? 'drag-active' : ''}`}
        onDragEnter={filesReadOnly ? undefined : handleDrag}
        onDragLeave={filesReadOnly ? undefined : handleDrag}
        onDragOver={filesReadOnly ? undefined : handleDrag}
        onDrop={filesReadOnly ? undefined : handleDrop}
        onClick={filesReadOnly ? undefined : () => !uploading && fileInputRef.current?.click()}
      >
        <div className="upload-area-content">
          <div className="upload-icon">📁</div>
          {filesReadOnly ? (
            <>
              <p className="upload-text">View and download only</p>
              <p className="upload-hint">Uploading and deleting files requires a full-access account.</p>
            </>
          ) : (
            <>
              <p className="upload-text">
                {dragActive ? 'Drop files here' : 'Drag and drop files here or click to browse'}
              </p>
              <p className="upload-hint">Multiple files supported</p>
            </>
          )}
        </div>
      </div>

      {files.length === 0 ? (
        <div className="empty-state">
          <p>
            {filesReadOnly
              ? 'No files or folders in this folder.'
              : 'No files or folders yet. Upload your first file!'}
          </p>
        </div>
      ) : (
        <div className="files-table-container">
          <table className="files-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Type</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => (
                <tr key={index} className={`file-row ${file.isFolder ? 'folder-row' : ''}`}>
                  <td className="file-name-cell">
                    <div className="file-name-wrapper">
                      <span className="file-icon">
                        {file.isFolder ? '📁' :
                         file.contentType?.startsWith('image/') ? '🖼️' :
                         file.contentType?.includes('pdf') ? '📄' :
                         file.contentType?.includes('word') || file.name.endsWith('.docx') || file.name.endsWith('.doc') ? '📝' :
                         file.contentType?.includes('excel') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? '📊' :
                         file.contentType?.includes('zip') || file.name.endsWith('.zip') ? '📦' :
                         '📎'}
                      </span>
                      {file.isFolder ? (
                        <button
                          className="file-name-link"
                          onClick={() => handleFolderClick(file.fullPath)}
                          title={`Open folder: ${file.name}`}
                        >
                          {file.name}
                        </button>
                      ) : (
                        <span className="file-name" title={file.name}>
                          {file.name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="file-size-cell">
                    {file.isFolder ? '-' : formatFileSize(file.size)}
                  </td>
                  <td className="file-type-cell">
                    {file.isFolder ? 'Folder' : (file.contentType || 'Unknown')}
                  </td>
                  <td className="file-date-cell" title={file.timeCreated}>
                    {formatDate(file.timeCreated)}
                  </td>
                  <td className="file-actions-cell">
                    {!file.isFolder && (
                      <button
                        className="btn-download"
                        onClick={() => handleDownload(file)}
                        title="Download/View"
                        disabled={!file.downloadURL}
                      >
                        ⬇️
                      </button>
                    )}
                    {!filesReadOnly && (
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteClick(file.name, file.fullPath, file.isFolder)}
                        title="Delete"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete {deleteConfirm.isFolder ? 'Folder' : 'File'}</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteConfirm.fileName}</strong>?</p>
            {deleteConfirm.isFolder && (
              <p className="warning-text">⚠️ This will delete the folder and all its contents.</p>
            )}
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                Delete {deleteConfirm.isFolder ? 'Folder' : 'File'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Files
